import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import React from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { To, useNavigate } from 'react-router-dom';
import companyLogo from '../../assets/truss-us-logo.png';
import { useNavigationBlocker } from '../../hooks';
import { logError } from '../../utils';

export interface EntityProfilePageProps<TFormValues extends FieldValues, TEntity> {
  /**
   * The title to display at the top of the profile page
   */
  readonly title: string;

  /**
   * The entity data returned from the API.
   * While not directly used in the base component,
   * this provides type safety and is available for
   * derived components that may need access to the entity.
   */
  readonly entity: TEntity | undefined;

  /**
   * Whether the entity data is currently loading
   */
  readonly isLoading: boolean;

  /**
   * Any error that occurred when loading the entity
   */
  readonly error: unknown;

  /**
   * Whether a save operation is in progress
   */
  readonly isSaving: boolean;

  /**
   * The form instance from React Hook Form
   */
  readonly form: UseFormReturn<TFormValues>;

  /**
   * Whether the form or image has been changed
   */
  readonly hasChanges: boolean;

  /**
   * Whether the form fields pass validation
   */
  readonly isValid?: boolean;

  /**
   * The name to use for image-related props and IDs
   * Optional - only needed when using image upload functionality
   */
  readonly imageName?: string;

  /**
   * The URL of the entity's image/avatar/logo
   * Optional - only needed when using image upload functionality
   */
  readonly imageUrl?: string;

  /**
   * Alt text for the image
   * Optional - only needed when using image upload functionality
   */
  readonly imageAlt?: string;

  /**
   * Button text for the image upload
   * Optional - only needed when using image upload functionality
   */
  readonly uploadButtonText?: string;

  /**
   * Whether the image should be displayed as rounded (square by default)
   */
  readonly roundedImage?: boolean;

  /**
   * Whether the image is a general logo or avatar (avatar by default)
   */
  readonly isLogo?: boolean;

  /**
   * Whether the form is in read-only mode
   */
  readonly isReadOnly?: boolean;

  /**
   * The width of the container. Defaults to 'md'.
   * This can be used to control the overall width of the profile page.
   */
  readonly pageWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;

  /**
   * Handler for form submission
   */
  readonly onSubmit: (data: TFormValues) => Promise<void>;

  /**
   * Handler for image change
   * Optional - only needed when using image upload functionality
   */
  readonly onImageChange?: (file: File) => void;

  /**
   * The content of the form fields
   */
  readonly children: React.ReactNode;
}

/**
 * A reusable data entry page component suitable for any form-based content.
 * Provides a consistent layout and behavior for entity management forms, settings pages,
 * or any other data entry screens. Optionally supports image upload functionality.
 */
const EntityProfilePage = <TFormValues extends FieldValues, TEntity>({
  title,
  entity, // Used for type safety and can be referenced in extended implementations
  isLoading,
  error,
  isSaving,
  form,
  hasChanges,
  isValid,
  imageName,
  imageUrl,
  imageAlt,
  uploadButtonText,
  roundedImage = false,
  isLogo = false,
  pageWidth = 'md',
  isReadOnly = false,
  onSubmit,
  onImageChange,
  children,
}: EntityProfilePageProps<TFormValues, TEntity>) => {
  const navigate = useNavigate();

  // Navigation blocking for unsaved changes
  const { showDialog, handleConfirmNavigation, handleCancelNavigation, navigateSafely } =
    useNavigationBlocker(hasChanges);

  const handleNavigateBack = () => {
    navigate(-1);
  };

  // Handle image file selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0] && onImageChange) {
      const file = e.target.files[0];
      onImageChange(file);
    }
  };

  // Submit handler that wraps the provided onSubmit with error handling
  const handleSubmit = async (data: TFormValues) => {
    try {
      await onSubmit(data);
      navigateSafely(-1 as To);
    } catch (error) {
      logError(`Error saving ${title.toLowerCase()}:`, error);
    }
  };

  if (isLoading) {
    return <Typography variant="body2">Loading {title.toLowerCase()} data...</Typography>;
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Error loading {title.toLowerCase()} data
      </Typography>
    );
  }

  return (
    <Container maxWidth={pageWidth}>
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, position: 'relative' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" component="h1" sx={{ fontSize: '1.2rem' }}>
              {title}
            </Typography>
          </Box>
          <IconButton
            onClick={handleNavigateBack}
            sx={{ position: 'absolute', top: -10, right: -10 }}
            aria-label="close"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Grid container spacing={3}>
            {/* Image upload section - only rendered if all required image props are provided */}
            {imageName && uploadButtonText && onImageChange && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Avatar
                  src={imageUrl || (isLogo ? companyLogo : undefined)}
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 1.5,
                    '& .MuiSvgIcon-root': {
                      width: '60%',
                      height: '60%',
                    },
                  }}
                  alt={imageAlt ?? ''}
                  variant={roundedImage ? 'circular' : 'rounded'}
                >
                  {!imageUrl && !isLogo && <PersonIcon fontSize="large" />}
                </Avatar>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id={`${imageName}-upload`}
                  type="file"
                  onChange={handleImageChange}
                />
                {!isReadOnly && (
                  <label htmlFor={`${imageName}-upload`}>
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon fontSize="small" />}
                      size="small"
                    >
                      {uploadButtonText}
                    </Button>
                  </label>
                )}
              </Grid>
            )}

            {/* Entity details section - expands to full width when no image section */}
            <Grid item xs={12} md={imageName && uploadButtonText && onImageChange ? 8 : 12}>
              {children}
            </Grid>

            {/* Submit button - only shown when not in read-only mode */}
            {!isReadOnly && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon fontSize="small" />}
                  disabled={isSaving || (!!entity && !hasChanges) || !isValid}
                  size="small"
                >
                  {isSaving ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showDialog}
        onClose={handleCancelNavigation}
        aria-labelledby="unsaved-dialog-title"
        aria-describedby="unsaved-dialog-description"
      >
        <DialogTitle id="unsaved-dialog-title" sx={{ fontSize: '1rem', py: 1.5 }}>
          Unsaved Changes
        </DialogTitle>
        <DialogContent id="unsaved-dialog-description">
          <Typography variant="body2">
            You have unsaved changes. Are you sure you want to leave this page? Your changes will be
            lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation} color="primary" size="small">
            Stay
          </Button>
          <Button onClick={handleConfirmNavigation} color="error" size="small">
            Leave
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EntityProfilePage;
